/**
 * A ficha de um item no estoque: os três números, o histórico e a correção.
 *
 * UMA TELA SÓ, e não duas, porque na prática são um trabalho só: ninguém
 * corrige um saldo sem antes olhar de onde ele veio. Separar em "histórico" e
 * "corrigir" obrigaria a pessoa a decidir o que quer antes de saber.
 *
 * O QUE ESTA TELA ENSINA, sem dar aula: **saldo não se edita**. O campo pede
 * quanto você CONTOU na prateleira; o sistema calcula a diferença e lança um
 * movimento de ajuste com justificativa. Nada é sobrescrito, e o ajuste passa
 * a aparecer no próprio histórico logo abaixo — que é a prova de que o número
 * continua explicável. Se houvesse um campo "novo saldo" gravando direto, o
 * rastro morreria ali.
 *
 * A COLUNA "ACUMULADO" É O CORAÇÃO. Ela mostra o saldo depois de cada
 * movimento, então dá para descer a lista e ver exatamente onde o número
 * tomou o rumo errado. É o que transforma "o saldo está errado" em "no dia 12
 * saiu 40 e deveria ser 4".
 */

import { api, ErroApi } from '../api.js';
import { escapar, numero, dataHora } from '../comum/formato.js';

interface Movimento {
  id: number;
  tipo: 'entrada' | 'saida';
  origem: string;
  quantidade: number;
  ocorrido_em: string;
  operador_nome: string | null;
  obs: string | null;
  fornecedor: string | null;
  numero_nf: string | null;
  lote: string | null;
  validade: string | null;
  obra: string | null;
  romaneio: string | null;
  acumulado: number;
}

interface Extrato {
  item: { codigo: string; nome: string; saldo: number; unidade: string | null };
  /** Falso = o acumulado dos movimentos não fecha com o saldo gravado. */
  confere: boolean;
  movimentos: Movimento[];
}

interface Numeros {
  codigo: string;
  nome: string;
  unidade: string | null;
  fisico: number;
  comprometido: number;
  disponivel: number;
}

/** De onde veio o movimento, em português de gente. */
const ORIGEM: Record<string, string> = {
  nota_fiscal: 'Nota fiscal',
  sem_nota: 'Sem nota fiscal',
  manual: 'Lançamento manual',
  romaneio: 'Romaneio',
  ajuste: 'Ajuste de inventário',
  descarte: 'Descarte',
  retorno: 'Retorno de entrega',
};

export function montarExtrato(
  raiz: HTMLElement,
  codigo: string,
  aoVoltar: () => void,
  abrirCorrecao = false,
): void {
  raiz.innerHTML = `<p class="carregando">Abrindo ${escapar(codigo)}…</p>`;
  void carregar();

  async function carregar() {
    try {
      const [extrato, numeros] = await Promise.all([
        api.get<Extrato>(`/api/itens/${encodeURIComponent(codigo)}/extrato`),
        api.get<Numeros>(`/api/estoque/${encodeURIComponent(codigo)}`),
      ]);
      desenhar(extrato, numeros);
    } catch (erro) {
      raiz.innerHTML = `<p class="aviso">${escapar(
        erro instanceof ErroApi ? erro.message : 'Não consegui abrir o histórico.',
      )}</p>`;
    }
  }

  function desenhar(e: Extrato, n: Numeros) {
    const un = escapar(n.unidade ?? '');

    raiz.innerHTML = `
      <button class="voltar" id="voltar">← Voltar ao estoque</button>
      <article class="ficha">
        <header class="ficha-topo">
          <div>
            <span class="rotulo">${escapar(e.item.codigo)}</span>
            <h2>${escapar(e.item.nome)}</h2>
          </div>
        </header>

        <dl class="ficha-dados">
          <div><dt>Físico</dt><dd class="${classe(n.fisico)}">${numero(n.fisico)} ${un}</dd></div>
          <div><dt>Comprometido</dt><dd>${numero(n.comprometido)} ${un}</dd></div>
          <div><dt>Disponível</dt><dd class="${classe(n.disponivel)}"><b>${numero(n.disponivel)} ${un}</b></dd></div>
          <div><dt>Movimentos</dt><dd>${e.movimentos.length}</dd></div>
        </dl>

        ${
          e.confere
            ? ''
            : `<p class="aviso"><b>Atenção:</b> a soma dos movimentos não fecha com o saldo gravado.
                 Isso não deveria acontecer — avise antes de corrigir, porque corrigir aqui
                 esconderia a causa em vez de resolvê-la.</p>`
        }

        <section class="corr" id="corr" ${abrirCorrecao ? '' : 'hidden'}>
          <h3>⚖️ Corrigir o saldo</h3>
          <p class="dica">
            O saldo não é editado — nunca. Informe <b>quanto você contou na prateleira</b>;
            o sistema lança a diferença como ajuste, com a sua justificativa, e ele passa a
            aparecer no histórico aqui embaixo.
          </p>
          <div class="conf-campos">
            <label class="rotulado estreito">
              <span>Saldo no sistema</span>
              <input value="${numero(n.fisico)}" readonly>
            </label>
            <label class="rotulado estreito">
              <span>Quanto você contou</span>
              <input type="number" step="any" id="contado" placeholder="ex.: 8">
            </label>
            <div class="rotulado estreito">
              <span>Diferença</span>
              <p class="corr-dif" id="dif">—</p>
            </div>
          </div>
          <label class="rotulado">
            <span>Justificativa</span>
            <input id="motivo" maxlength="1000" placeholder="Ex.: contagem do inventário de agosto; 3 unidades quebradas no galpão">
          </label>
          <div class="conf-acoes">
            <button class="btn" id="aplicar">Lançar o ajuste</button>
            <button class="btn btn-claro" id="cancelar">Cancelar</button>
          </div>
          <p class="aviso" id="corr-erro" hidden></p>
        </section>

        ${abrirCorrecao ? '' : `<div class="conf-acoes"><button class="btn btn-claro" id="abrir-corr">⚖️ Corrigir o saldo</button></div>`}

        <h3>Histórico</h3>
        ${
          e.movimentos.length === 0
            ? `<div class="vazio"><p>Nenhum movimento ainda — este item nunca entrou nem saiu.</p></div>`
            : `<p class="dica">Do mais antigo para o mais recente. A coluna <b>Acumulado</b> é o saldo
                 depois de cada movimento — é por ela que se acha onde o número tomou o rumo errado.</p>
               <div class="rolagem">
                 <table class="tabela">
                   <thead><tr>
                     <th>Quando</th><th>O quê</th><th class="num">Qtd.</th>
                     <th class="num">Acumulado</th><th>Detalhe</th><th>Quem</th>
                   </tr></thead>
                   <tbody>${e.movimentos.map((m) => linha(m, un)).join('')}</tbody>
                 </table>
               </div>`
        }
      </article>`;

    raiz.querySelector('#voltar')!.addEventListener('click', aoVoltar);
    raiz.querySelector('#abrir-corr')?.addEventListener('click', () => {
      const corr = raiz.querySelector<HTMLElement>('#corr')!;
      corr.hidden = false;
      corr.scrollIntoView({ block: 'nearest' });
      raiz.querySelector<HTMLInputElement>('#contado')!.focus();
    });
    ligarCorrecao(n);
  }

  function linha(m: Movimento, un: string): string {
    const entrada = m.tipo === 'entrada';
    // O detalhe muda com a origem: de romaneio interessa a obra, de nota
    // interessa o fornecedor. Uma coluna genérica esconderia os dois.
    const detalhe = [
      m.romaneio ? `Romaneio ${escapar(m.romaneio)}` : '',
      m.obra ? `Obra ${escapar(m.obra)}` : '',
      m.fornecedor ? escapar(m.fornecedor) : '',
      m.numero_nf ? `NF ${escapar(m.numero_nf)}` : '',
      m.lote ? `lote ${escapar(m.lote)}` : '',
      m.obs ? escapar(m.obs) : '',
    ]
      .filter(Boolean)
      .join(' · ');

    return `
      <tr class="${m.origem === 'ajuste' ? 'mov-ajuste' : ''}">
        <td class="data">${dataHora(m.ocorrido_em)}</td>
        <td>
          <span class="mov-tipo ${entrada ? 'mov-e' : 'mov-s'}">${entrada ? 'entrada' : 'saída'}</span>
          <span class="sub">${escapar(ORIGEM[m.origem] ?? m.origem)}</span>
        </td>
        <td class="num ${entrada ? 'mov-mais' : 'mov-menos'}">${entrada ? '+' : '−'}${numero(m.quantidade)}</td>
        <td class="num"><b>${numero(m.acumulado)}</b> <span class="card-un">${un}</span></td>
        <td class="mov-detalhe">${detalhe || '—'}</td>
        <td>${escapar(m.operador_nome ?? '—')}</td>
      </tr>`;
  }

  function ligarCorrecao(n: Numeros) {
    const contado = raiz.querySelector<HTMLInputElement>('#contado');
    if (!contado) return;
    const dif = raiz.querySelector<HTMLElement>('#dif')!;
    const erro = raiz.querySelector<HTMLElement>('#corr-erro')!;

    /**
     * Mostra a diferença enquanto a pessoa digita.
     *
     * É o ponto todo: ela confirma sabendo o efeito. Sem isto, "contei 8"
     * numa tela que já dizia 5 vira um número solto, e só depois de gravar
     * alguém descobre que foram 3 a mais e não 8 a mais.
     */
    contado.addEventListener('input', () => {
      const v = contado.value.trim();
      if (v === '' || !Number.isFinite(Number(v))) {
        dif.textContent = '—';
        dif.className = 'corr-dif';
        return;
      }
      const d = Number(v) - n.fisico;
      dif.textContent = d === 0 ? 'nada a ajustar' : `${d > 0 ? '+' : '−'}${numero(Math.abs(d))}`;
      dif.className = `corr-dif ${d > 0 ? 'mov-mais' : d < 0 ? 'mov-menos' : ''}`;
    });

    raiz.querySelector('#cancelar')!.addEventListener('click', () => {
      raiz.querySelector<HTMLElement>('#corr')!.hidden = true;
    });

    raiz.querySelector('#aplicar')!.addEventListener('click', async () => {
      const v = Number(contado.value);
      const motivo = raiz.querySelector<HTMLInputElement>('#motivo')!.value.trim();
      if (contado.value.trim() === '' || !Number.isFinite(v)) {
        return mostrar(erro, 'Informe quanto você contou.');
      }
      if (v === n.fisico) {
        return mostrar(erro, 'O contado é igual ao do sistema — não há o que ajustar.');
      }
      if (motivo.length < 3) {
        return mostrar(erro, 'Escreva a justificativa. É ela que explica o ajuste daqui a seis meses.');
      }

      const botao = raiz.querySelector<HTMLButtonElement>('#aplicar')!;
      botao.disabled = true;
      botao.textContent = 'Lançando…';
      try {
        const r = await api.post<{ saldoAnterior: number; saldoNovo: number; diferenca: number }>(
          '/api/movimentos/ajuste',
          { codigo, saldoContado: v, justificativa: motivo },
        );
        // Recarrega tudo: o ajuste precisa APARECER no histórico logo abaixo —
        // é a prova visível de que nada foi sobrescrito.
        raiz.innerHTML = `<p class="carregando">Ajuste lançado (${
          r.diferenca > 0 ? '+' : '−'
        }${numero(Math.abs(r.diferenca))}). Recarregando…</p>`;
        await carregar();
      } catch (e) {
        botao.disabled = false;
        botao.textContent = 'Lançar o ajuste';
        mostrar(erro, e instanceof ErroApi ? e.message : 'Não consegui lançar o ajuste.');
      }
    });
  }

  function mostrar(el: HTMLElement, msg: string) {
    el.textContent = msg;
    el.hidden = false;
  }

  function classe(v: number): string {
    return v < 0 ? 'negativo' : v === 0 ? 'zero' : '';
  }
}
